import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Headset, Plus } from 'lucide-react'
import { ReportIssueDialog } from '@/components/support/ReportIssueDialog'
import { SupportConversation } from '@/components/support/SupportConversation'
import { SupportFacts } from '@/components/support/SupportFacts'
import { SupportInbox } from '@/components/support/SupportInbox'
import { Button } from '@/components/ui/button'
import { useSupportStatus, useSupportThread, useSupportTicket, useSupportTickets } from '@/hooks/useSupport'
import { NOT_CONNECTED, matchesChip, matchesSearch, type InboxChip } from '@/lib/support/supportView'
import { PERMISSIONS, usePermission } from '@/lib/permissions'
import { useTenant } from '@/providers/TenantProvider'

/**
 * /support and /support/:number — report an issue to Title21 and follow it:
 * inbox on the left, the conversation in the middle, the facts on the right.
 * Everything is this tenant's only (the backend names the X-Tenant-Id tenant).
 */
export function SupportPage() {
  const { number } = useParams()
  const navigate = useNavigate()
  const { selectedTenant } = useTenant()
  const { can } = usePermission()
  const canCreate = can(PERMISSIONS.supportCreate)
  const status = useSupportStatus()
  const connected = status.data?.connected ?? false
  const tickets = useSupportTickets(connected)
  const ticket = useSupportTicket(connected ? number : undefined)
  const thread = useSupportThread(connected ? number : undefined)
  const [chip, setChip] = useState<InboxChip>('mine')
  const [search, setSearch] = useState('')
  const [reporting, setReporting] = useState(false)
  const now = useNow()

  const all = useMemo(() => tickets.data?.items ?? [], [tickets.data])
  const shown = all.filter((t) => matchesChip(t, chip) && matchesSearch(t, search))
  const waitingCount = all.filter((t) => t.status === 'waiting_on_customer').length

  // Nothing of mine yet but the tenant has tickets: start on "All of this tenant".
  useEffect(() => {
    if (chip === 'mine' && all.length > 0 && !all.some((t) => t.raisedByMe)) setChip('all')
  }, [all, chip])

  return (
    <div className='flex h-[calc(100vh-3.5rem)] flex-col gap-3 p-4'>
      <div className='flex items-center justify-between gap-3'>
        <div className='flex items-center gap-3'>
          <span className='flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10 text-primary'>
            <Headset className='h-5 w-5' aria-hidden />
          </span>
          <div>
            <h1 className='text-lg font-bold text-foreground'>Support</h1>
            <p className='text-xs text-muted-foreground'>
              {selectedTenant?.name ?? 'This tenant'} · Title21 Support{status.data?.customer ? ` · ${status.data.customer}` : ''}
            </p>
          </div>
        </div>
        {canCreate ? (
          <Button onClick={() => setReporting(true)} disabled={!connected}>
            <Plus className='h-4 w-4' />
            Report an issue
          </Button>
        ) : null}
      </div>

      {status.isLoading ? (
        <p className='text-sm text-muted-foreground'>Connecting to Title21 Support…</p>
      ) : !connected ? (
        <div className='rounded-xl border border-amber-200 bg-amber-50 px-5 py-6 text-sm text-amber-900'>
          {NOT_CONNECTED[status.data?.reason ?? 'unreachable']}
        </div>
      ) : (
        <div className='grid min-h-0 flex-1 grid-cols-[320px_1fr_260px] overflow-hidden rounded-xl border border-border bg-background'>
          <SupportInbox
            tickets={shown}
            selected={number}
            chip={chip}
            onChip={setChip}
            search={search}
            onSearch={setSearch}
            waitingCount={waitingCount}
            now={now}
            loading={tickets.isLoading}
          />
          {number && ticket.data ? (
            <>
              <SupportConversation ticket={ticket.data} messages={thread.data ?? []} canReply={canCreate} now={now} onReportNew={() => setReporting(true)} />
              <SupportFacts ticket={ticket.data} now={now} />
            </>
          ) : (
            <div className='col-span-2 flex flex-col items-center justify-center gap-3 p-8 text-center text-sm text-muted-foreground'>
              {number && ticket.isLoading ? (
                'Loading the ticket…'
              ) : number && ticket.error ? (
                <>
                  This ticket isn't one of {selectedTenant?.name ?? 'this tenant'}'s.
                  <Button variant='outline' size='sm' onClick={() => navigate('/support')}>
                    Back to Support
                  </Button>
                </>
              ) : (
                <>
                  <Headset className='h-8 w-8' aria-hidden />
                  Pick a ticket to follow it, or report a new issue to Title21.
                </>
              )}
            </div>
          )}
        </div>
      )}

      <ReportIssueDialog open={reporting} onOpenChange={setReporting} fromUrl={window.location.href} />
    </div>
  )
}

/** The current time, ticking every 30 s so "5 min ago" stays true. */
function useNow(): number {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 30_000)
    return () => window.clearInterval(id)
  }, [])
  return now
}
